import React, { useEffect, useRef } from "react";
import * as Scenes from "oc-threejs/Scenes";

import { v3 } from "oc-threejs/short3";
import classnames from "classnames";
import { OrbitControlsOC } from "oc-threejs/patched/OrbitControlsOC";
import {
  BoxGeometry,
  Color,
  Light,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  SRGBColorSpace,
  Scene,
  SphereGeometry,
  WebGLRenderer,
} from "three";

export const PreviewStage = ({
  title = null,
  className,
  initStage = (stage: PreviewRenderer, container: Element) => {},
  onDispose = () => {},
  children = null,
  changeDependencies,
  appScene,
}) => {
  const containerRef = useRef(null);
  useEffect(() => {
    const container = containerRef.current;
    let canvasRect = container.getBoundingClientRect();
    if (canvasRect.width * canvasRect.height < 1) {
      return onDispose;
    }
    const stage = new PreviewRenderer();
    stage.setSize(canvasRect.width, canvasRect.height);
    container.append(stage.renderer.domElement);
    stage.startRenderLoop();
    initStage(stage, container);
    stage.copyEnvironment(appScene);
    return () => {
      onDispose();
      stage.dispose();
    };
  }, changeDependencies);
  return (
    <div className={classnames("preview-stage", className)} title={title} ref={containerRef}>
      {children}
    </div>
  );
};

export class PreviewRenderer {
  static cachedRenderer;

  renderer;
  scene;
  camera;
  controls;

  isDirty = true;
  isDirtyUntil = 0;
  renderLooping = false;

  constructor() {
    this.initRenderer();
    this.initScene();
    this.initCamera();
    this.initControls();
    this.setDirtyForMs(1000);
  }

  initScene() {
    this.scene = new Scene();
  }

  initRenderer() {
    if (PreviewRenderer.cachedRenderer) {
      this.renderer = PreviewRenderer.cachedRenderer;
    } else {
      PreviewRenderer.cachedRenderer = this.renderer = new WebGLRenderer({
        antialias: true,
        preserveDrawingBuffer: true,
        alpha: true,
        logarithmicDepthBuffer: true,
      });
      this.renderer.setClearColor(new Color(0x000000), 0);
      ["toneMapping", "toneMappingExposure", "outputColorSpace"].forEach((key) => {
        this.renderer[key] = APP.renderer[key];
      });
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = PCFSoftShadowMap;
      /*
                https://threejs.org/docs/#examples/en/loaders/GLTFLoader
                Textures containing color information (.map, .emissiveMap, and .specularMap) always use sRGB colorspace in glTF, while vertex colors and material properties (.color, .emissive, .specular) use linear colorspace. In a typical rendering workflow, textures are converted to linear colorspace by the renderer, lighting calculations are made, then final output is converted back to sRGB and displayed on screen. Unless you need post-processing in linear colorspace, always configure WebGLRenderer as follows when using glTF:

                renderer.outputColorSpace = THREE.SRGBColorSpace;
                GLTFLoader will automatically configure textures referenced from a .gltf or .glb file correctly, with the assumption that the renderer is set up as shown above. When loading textures externally (e.g., using TextureLoader) and applying them to a glTF model, colorspace and orientation must be given:

                // If texture is used for color information, set colorspace.
                texture.colorSpace = THREE.SRGBColorSpace;

                // UVs use the convention that (0, 0) corresponds to the upper left corner of a texture.
                texture.flipY = false;
            */
    }
  }

  initCamera() {
    this.camera = new PerspectiveCamera(45, 1, 0.0000001, 1000000000);
    this.camera.position.copy(v3(0, 2, 5));
    this.scene.add(this.camera);
  }

  initControls() {
    this.controls = new OrbitControlsOC(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.07;
    this.controls.addEventListener("change", () => this.setDirtyForMs(600));
  }

  autoRotate() {
    this.controls.autoRotate = true;
    this.controls.domElement.addEventListener(
      "pointerdown",
      () => {
        if (this.controls) {
          this.controls.autoRotate = false;
        }
      },
      { once: true },
    );
  }

  zoomTo(object3d, { factor = 3, direction = v3(0, 2, 5).normalize() } = {}) {
    const sphere = Scenes.boundingSphere(object3d);
    // const sphere = new THREE.Box3().setFromObject(object3d).getBoundingSphere(new THREE.Sphere());
    this.controls.target.copy(sphere.center);
    this.controls.object.position.copy(direction.multiplyScalar(sphere.radius * factor));
  }

  copyEnvironment(parentScene) {
    this.scene.environment = parentScene.environment;
    Scenes.filter(parentScene, (x) => x instanceof Light).forEach((x) => {
      this.scene.add(x.clone());
    });
  }

  setSize(width, height) {
    if (width < 1 || height < 1) {
      throw new Error("width or height < 1");
    }
    this.camera.aspect = width / height;
    this.renderer.setPixelRatio(window.devicePixelRatio ?? 1);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  render() {
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  setDirtyForMs(ms) {
    this.isDirtyUntil = Math.max(performance.now() + ms, this.isDirtyUntil);
  }

  dirty() {
    this.isDirty = true;
  }

  startRenderLoop() {
    const render = (timestamp) => {
      if (this.renderer) {
        requestAnimationFrame(render);
        if (this.isDirty || this.renderLooping || this.isDirtyUntil > timestamp) {
          this.isDirty = false;
          this.render();
        }
      }
    };
    render(0);
  }

  dispose() {
    this.renderer = null;
    this.camera = null;
    this.scene = null;
    this.controls = null;
  }

  addGridSkyAndGround(size) {
    const skyRadius = size * 100;

    const skyMaterial = new MeshBasicMaterial({
      fog: false,
      transparent: true,
      opacity: 0.03,
      color: 0xffffff,
      wireframe: true,
      wireframeLinewidth: 10,
    });
    const skyGeometry = new SphereGeometry(skyRadius, 10, 10);
    const sky = new Mesh(skyGeometry, skyMaterial);
    sky.name = "sky";
    sky.raycastVisibleTags = [];
    sky.frustumCulled = false;
    (sky as any).isSceneBackground = true;
    this.scene.add(sky);

    const groundRadius = size * 20;
    const groundMaterial = new MeshBasicMaterial({
      fog: false,
      transparent: true,
      opacity: 0.03,
      color: 0x000000,
      wireframe: true,
      wireframeLinewidth: 1,
    });
    const groundGeometry = new PlaneGeometry(groundRadius, groundRadius, 20, 20);
    const ground = new Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = Math.PI / 2;
    ground.name = "ground";
    ground.raycastVisibleTags = [];
    ground.frustumCulled = false;
    (ground as any).isSceneBackground = true;
    this.scene.add(ground);
  }

  addBackgroundBoxes(size) {
    const groundRadius = size * 20;
    const box = (x, y, h) => {
      const boxGeometry = new BoxGeometry(0.5, 3, 0.5);
      const boxMaterial = new MeshStandardMaterial({
        color: new Color().setHSL(h / 20, 0.1, 0.5, SRGBColorSpace),
      });
      const box = new Mesh(boxGeometry, boxMaterial);
      box.position.x = x;
      box.position.y = 1.5;
      box.position.z = y;
      this.scene.add(box);
    };
    const boxesPerSide = 10;
    const boxRand = groundRadius;
    for (let i = 1; i < boxesPerSide; i++) {
      const x = -boxRand / 2 + (i / boxesPerSide) * boxRand;
      const y = -boxRand / 2;
      box(x, y, i);
      box(y, x, i);
      box(x, -y, -i);
      box(-y, x, -i);
    }
  }
}
